/* ════════════════════════════════════════════════════════════════════════
   SCHRIFTBOT — the plan ladder. Single source of truth.
   ────────────────────────────────────────────────────────────────────────
   Before this file existed the app shipped TWO different price lists and
   the same tier was sold at two different prices depending on which modal
   the visitor happened to open. Both modals now render from the ladder
   below, so there is exactly one answer.

   Two tiers, not three: a year of live data (three different attempts at
   a middle tier — 3,49 €, 6,49 €, both in a three-box layout — sold zero)
   showed that a third box between "cheap" and "everything" gets ignored
   regardless of its price. The two that remain were proven live, together,
   in the same view (2,99 € + a higher Unlimited price). Small price gap
   to Unlimited, large value gap (fixed downloads vs. unlimited + own
   handwriting) — deliberate, not incidental.

   Any Stripe price referenced anywhere must exist and be active in
   Stripe, and any price removed here should be archived in Stripe so
   nobody can reach an orphaned checkout link. Existing subscribers on a
   retired price (Basic 0,99 €, Unlimited 9,99 €/7,99 €, Pass 6,49 €) keep
   paying that price until they cancel — those price IDs stay valid in
   Stripe and in the backend's subscription handling, they are just no
   longer offered to new customers here.
   ════════════════════════════════════════════════════════════════════════ */

/* Everything the § 312j Abs. 2 BGB summary has to state, in one place, so
   the checkout sheet and the plan cards cannot drift apart. */
export const CONTRACT_TERMS = {
  interval: "Monat",
  intervalLabel: "pro Monat",
  /* Essential characteristics / duration / termination — shown verbatim
     directly above the order button. */
  duration:
    "Monatsabo. Es verlängert sich automatisch um je einen Monat, bis du kündigst.",
  termination:
    "Jederzeit zum Ende des laufenden Abrechnungsmonats kündbar — ohne Angabe von Gründen, über den Kündigen-Button und ohne Anmeldung.",
  // Kleinunternehmer: Endpreise ohne Umsatzsteuer, Hinweis nach § 19 UStG.
  vat: "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet. Abrechnung monatlich im Voraus über Stripe.",
};

export const PLANS = [
  {
    id: "starter",
    name: "Starter",
    priceId: "price_1TC4In49gql0qC52FFnrr831",
    price: "2,99 €",
    priceNumeric: 2.99,
    summary: "Zum regelmäßigen Schreiben, mit festem Kontingent.",
    features: [
      { label: "30 Downloads pro Monat", on: true, highlight: true },
      { label: "Alle Handschriften", on: true },
      { label: "Kein Wasserzeichen", on: true },
      { label: "Eigene Handschrift", on: false },
      { label: "Export als GoodNotes-Datei", on: false },
    ],
    featured: false,
  },
  {
    id: "unlimited",
    name: "Unlimited",
    priceId: "price_1UCIXb49gql0qC52MBbmP1fx",
    price: "3,99 €",
    priceNumeric: 3.99,
    summary: "Alles offen, inklusive deiner eigenen Handschrift.",
    features: [
      { label: "Eigene Handschrift", on: true, highlight: true },
      { label: "Unbegrenzte Downloads", on: true, highlight: true },
      { label: "Export als GoodNotes-Datei", on: true, highlight: true },
      { label: "Alle Handschriften", on: true },
      { label: "Kein Wasserzeichen", on: true },
    ],
    featured: true,
  },
];

export const getPlanByPriceId = (priceId) =>
  PLANS.find((p) => p.priceId === priceId) || null;

/* Testphase: Unlimited, die ersten Tage gratis. Die Tage setzt das Backend
   im Checkout (TRIAL_DAYS in schriftbot-backend/index.js) — beide Zahlen
   müssen gleich bleiben. */
const unlimitedPlan = PLANS.find((p) => p.id === "unlimited");
export const TRIAL_PLAN = {
  ...unlimitedPlan,
  trialDays: 7,
};

/* Kündigung in der Testphase beendet sie sofort — samt Zugang. Das muss vor
   der Bestellung klar dastehen (Bestellübersicht, Testangebot, AGB). Ein
   Wortlaut für alle Stellen. */
export const TRIAL_CANCEL_TEXT =
  "Mit der Kündigung endet die Testphase sofort. Es fallen dabei keine Kosten an.";

/* Die roten Hinweise auf /kuendigen und /widerruf — für alle sichtbar, ob
   gerade eine Testphase läuft oder nicht, und vor dem Abschicken. */
export const TRIAL_CANCEL_WARNING =
  "In der kostenlosen Testphase: Mit der Kündigung endet die Testphase sofort. Damit endet auch dein Zugang sofort, nicht erst am Ende der Testphase.";
export const TRIAL_WITHDRAW_WARNING =
  "In der kostenlosen Testphase: Mit dem Widerruf endet die Testphase sofort. Damit endet auch dein Zugang sofort, nicht erst am Ende der Testphase.";

export const trialTerms = (plan) =>
  `Die ersten ${plan.trialDays} Tage sind kostenlos. ${TRIAL_CANCEL_TEXT} Nach dem Testzeitraum kostet das Abo ${plan.price} ${CONTRACT_TERMS.intervalLabel} und verlängert sich automatisch um je einen Monat, bis du kündigst.`;

export const TRIAL_TERMINATION =
  "Nach der Testphase jederzeit zum Ende des laufenden Abrechnungsmonats kündbar — ohne Angabe von Gründen, über den Kündigen-Button und ohne Anmeldung.";

export default PLANS;
